import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingApprovals = ({ requests, onRequestSelect, onQuickApprove, onQuickReject, currentUser }) => {
  const pendingRequests = requests?.filter(request => 
    request?.status === 'pending' && 
    (currentUser?.role === 'admin' || request?.reviewer === currentUser?.name)
  );

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getDaysOverdue = (submissionDate) => {
    const submitted = new Date(submissionDate);
    const now = new Date();
    const diffTime = now - submitted;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (pendingRequests?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">All Caught Up!</h3>
        <p className="text-sm text-muted-foreground">
          No pending approvals require your attention at this time.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Pending Approvals</h3>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">
              {pendingRequests?.length} pending
            </span>
          </div>
        </div>
      </div>
      {/* Requests List */}
      <div className="divide-y divide-border">
        {pendingRequests?.map((request) => {
          const daysOverdue = getDaysOverdue(request?.submissionDate);
          const isOverdue = daysOverdue > 3;

          return (
            <div key={request?.id} className="p-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-start gap-4">
                {/* Priority Indicator */}
                <div className="flex-shrink-0 mt-1">
                  <Icon 
                    name="AlertCircle" 
                    size={20} 
                    className={getPriorityColor(request?.priority)}
                  />
                </div>

                {/* Request Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground truncate">
                          {request?.documentName}
                        </h4>
                        <span className="font-mono text-xs text-primary">
                          {request?.requestId}
                        </span>
                        {isOverdue && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-error/10 text-error">
                            Overdue
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {request?.justification}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>By {request?.requester}</span>
                        <span>{formatDate(request?.submissionDate)}</span>
                        <span className={`capitalize ${getPriorityColor(request?.priority)}`}>
                          {request?.priority} priority
                        </span>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        iconSize={14}
                        onClick={() => onRequestSelect(request)}
                      >
                        Review
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        iconName="Check"
                        iconSize={14}
                        onClick={() => onQuickApprove(request?.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="X"
                        iconSize={14}
                        onClick={() => onQuickReject(request?.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>

                  {/* Progress Indicator */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Time since submission</span>
                      <span>{daysOverdue} days</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all ${
                          isOverdue ? 'bg-error' : daysOverdue > 2 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${Math.min(100, (daysOverdue / 5) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer */}
      <div className="p-3 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {pendingRequests?.filter(r => getDaysOverdue(r?.submissionDate) > 3)?.length} overdue requests
          </span>
          <Button variant="ghost" size="sm">
            View All Requests
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovals;